import { useMemo, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight } from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useReturnStats, useReturnRate, type ReturnRateRow } from "@/hooks/use-returns";
import { useActiveStore } from "@/hooks/use-active-store";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import {
  REASON_LABEL,
  REQUEST_TYPE_LABEL,
  STATUS_LABEL,
  STALE_DAYS,
  formatDaysAgo,
} from "@/lib/return-labels";
import { returnsAnalyticsRoute } from "@/lib/router";

const PERIODS = [
  { value: 7, label: "7 dana" },
  { value: 30, label: "30 dana" },
  { value: 90, label: "90 dana" },
];

/** Boja trake po statusu — ista porodica boja kao oznake statusa u listi zahtjeva. */
const STATUS_BAR: Record<string, string> = {
  new: "bg-blue-500",
  in_progress: "bg-yellow-500",
  approved: "bg-green-500",
  rejected: "bg-red-500",
  refund_completed: "bg-violet-500",
  exchange_completed: "bg-teal-500",
};

/** Razlozi koji govore da veličina nije odgovarala — iz njih se računa napomena ispod liste. */
const SIZE_REASONS = ["preveliko", "premalo", "ne_stoji"];

function share(count: number, total: number): string {
  if (!total) return "0%";
  return `${Math.round((count / total) * 100)}%`;
}

function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">{children}</div>
  );
}

function Panel({
  title,
  note,
  children,
  className,
}: {
  title: string;
  note?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn("ring-1 ring-foreground/10 p-4 flex flex-col gap-4 min-w-0", className)}>
      <div className="flex items-baseline justify-between gap-3">
        <Eyebrow>{title}</Eyebrow>
        {note && <span className="text-[11px] text-muted-foreground">{note}</span>}
      </div>
      {children}
    </section>
  );
}

function Empty({ children = "Nema podataka u ovom periodu" }: { children?: ReactNode }) {
  return <p className="text-[13px] text-muted-foreground">{children}</p>;
}

function RowsSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="flex flex-col gap-4">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-6 w-full" />
      ))}
    </div>
  );
}

/** Rangirana lista: naziv i brojevi u jednom redu, traka udjela preko pune širine ispod. */
function RankList({
  rows,
  total,
  isLoading,
  empty,
}: {
  rows: { key: string; label: string; count: number }[];
  total: number;
  isLoading: boolean;
  empty?: string;
}) {
  if (isLoading) return <RowsSkeleton />;
  if (rows.length === 0) return <Empty>{empty}</Empty>;
  const max = rows[0]?.count || 1;
  return (
    <ul className="flex flex-col gap-3.5">
      {rows.map((r, i) => (
        <li key={r.key} className="flex flex-col gap-1.5 min-w-0">
          <div className="flex items-baseline gap-3 min-w-0">
            <span className="text-[13px] truncate flex-1" title={r.label}>
              {r.label}
            </span>
            <span className="font-mono text-[13px] tabular-nums">{r.count}</span>
            <span className="font-mono text-[11px] tabular-nums text-muted-foreground w-9 text-right">
              {share(r.count, total)}
            </span>
          </div>
          <div className="h-1.5 w-full bg-foreground/[0.07]">
            <div
              className={cn("h-full", i === 0 ? "bg-foreground/70" : "bg-foreground/30")}
              style={{ width: `${(r.count / max) * 100}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

/** Jedna vodoravna traka podijeljena na segmente + legenda ispod. */
function SplitBar({
  rows,
  isLoading,
}: {
  rows: { key: string; label: string; count: number; color: string }[];
  isLoading: boolean;
}) {
  const total = rows.reduce((sum, r) => sum + r.count, 0);
  if (isLoading) return <RowsSkeleton rows={3} />;
  if (total === 0) return <Empty />;
  return (
    <div className="flex flex-col gap-3">
      <div className="flex h-2.5 w-full overflow-hidden bg-foreground/[0.07]">
        {rows.map((r) => (
          <div
            key={r.key}
            className={cn("h-full", r.color)}
            style={{ width: `${(r.count / total) * 100}%` }}
            title={`${r.label}: ${r.count}`}
          />
        ))}
      </div>
      <ul className="flex flex-col gap-2">
        {rows.map((r) => (
          <li key={r.key} className="flex items-baseline gap-2.5 text-[13px] min-w-0">
            <span className={cn("h-2 w-2 shrink-0 translate-y-[1px]", r.color)} />
            <span className="truncate flex-1">{r.label}</span>
            <span className="font-mono tabular-nums">{r.count}</span>
            <span className="font-mono text-[11px] tabular-nums text-muted-foreground w-9 text-right">
              {share(r.count, total)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TrendTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  const count = payload[0].value as number;
  return (
    <div className="ring-1 ring-foreground/15 bg-background px-2.5 py-1.5 text-[12px]">
      <div className="text-muted-foreground">{label}</div>
      <div className="font-mono tabular-nums">
        {count} {count === 1 ? "povrat" : "povrata"}
      </div>
    </div>
  );
}

/** „2026-08-20" → „20.08." — na osi trebaju samo dan i mjesec. */
function shortDate(iso: string): string {
  const [, m, d] = iso.split("-");
  return `${d}.${m}.`;
}

/** Kad se zamjena desila unutar istog proizvoda, naziv proizvoda ide u zaseban red iznad,
 *  a u zamjeni ostaju samo varijacije — inače se oba kraja skrate baš na dijelu koji je bitan. */
function variationSwap(from: string, to: string): { product: string; from: string; to: string } | null {
  const SEP = " · ";
  const [fromProduct, ...fromRest] = from.split(SEP);
  const [toProduct, ...toRest] = to.split(SEP);
  if (!fromProduct || fromProduct !== toProduct || fromRest.length === 0 || toRest.length === 0) return null;
  return { product: fromProduct, from: fromRest.join(SEP), to: toRest.join(SEP) };
}

export function ReturnsAnalyticsPage() {
  const search = returnsAnalyticsRoute.useSearch();
  const navigate = returnsAnalyticsRoute.useNavigate();
  const days = search.days ?? 30;

  const { storeId } = useActiveStore();
  const { data: stats, isLoading } = useReturnStats({ storeId: storeId || undefined, days });
  const {
    data: rateData,
    isLoading: rateLoading,
    error: rateError,
  } = useReturnRate({ storeId: storeId || undefined, days });

  const total = stats?.totalReturns ?? 0;
  const perDay = total ? Math.round((total / days) * 10) / 10 : 0;

  const trend = useMemo(
    () => (stats?.trend ?? []).map((p) => ({ ...p, label: shortDate(p.date) })),
    [stats?.trend],
  );
  const peak = useMemo(
    () => trend.reduce((max, p) => Math.max(max, p.count), 0),
    [trend],
  );

  const reasonRows = (stats?.byReason ?? []).map((r) => ({
    key: r.reason,
    label: REASON_LABEL[r.reason] || r.reason,
    count: r.count,
  }));
  const reasonTotal = (stats?.byReason ?? []).reduce((sum, r) => sum + r.count, 0);
  const sizeReasonCount = (stats?.byReason ?? [])
    .filter((r) => SIZE_REASONS.includes(r.reason))
    .reduce((sum, r) => sum + r.count, 0);

  const linked = stats?.byProduct ?? [];
  const unlinked = stats?.byProductUnlinked ?? [];
  // Zajednički nazivnik za obje liste proizvoda — inače bi "nepovezano" imalo
  // vlastitu skalu i 3 komada bi izgledala kao 27% svih povrata.
  const productTotal =
    linked.reduce((sum, p) => sum + p.count, 0) + unlinked.reduce((sum, p) => sum + p.count, 0);
  const variations = stats?.byVariation ?? [];
  const variationTotal = variations.reduce((sum, v) => sum + v.count, 0);
  const flow = stats?.exchangeFlow ?? [];

  return (
    <div className="p-4 flex flex-col gap-4 max-w-[1400px]">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col gap-1">
          <Link
            to="/returns"
            className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1.5 w-fit"
          >
            <ArrowLeft className="h-3 w-3" />
            Zahtjevi za povrat
          </Link>
          <h2 className="text-lg font-medium tracking-tight">Analitika povrata</h2>
        </div>

        <div className="flex ring-1 ring-foreground/10">
          {PERIODS.map((p) => (
            <button
              key={p.value}
              type="button"
              onClick={() => navigate({ search: { days: p.value } })}
              className={cn(
                "px-3 h-8 text-xs transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-foreground/40",
                p.value === days
                  ? "bg-foreground text-background"
                  : "text-muted-foreground hover:text-foreground hover:bg-foreground/5",
              )}
            >
              {p.label}
            </button>
          ))}
        </div>
      </header>

      {/* Obim: jedan vodeći broj lijevo, kretanje kroz period desno. */}
      <section className="ring-1 ring-foreground/10 grid grid-cols-1 lg:grid-cols-[minmax(0,280px)_1fr]">
        <div className="p-5 flex flex-col gap-5 lg:border-r border-b lg:border-b-0 border-foreground/10">
          <div className="flex flex-col gap-2">
            <Eyebrow>Zahtjeva u periodu</Eyebrow>
            {isLoading ? (
              <Skeleton className="h-12 w-24" />
            ) : (
              <div className="flex items-baseline gap-2.5">
                <span className="font-mono text-5xl leading-none tracking-tight tabular-nums">
                  {total}
                </span>
                <span className="text-[13px] text-muted-foreground">
                  {perDay} dnevno
                </span>
              </div>
            )}
          </div>

          <dl className="flex flex-col gap-2.5 text-[13px]">
            <div className="flex items-baseline justify-between gap-3">
              <dt className="text-muted-foreground">Neriješeno</dt>
              <dd className="font-mono tabular-nums">{stats?.unresolvedCount ?? 0}</dd>
            </div>
            <div className="flex items-baseline justify-between gap-3">
              <dt className="text-muted-foreground">Starije od {STALE_DAYS} dana</dt>
              <dd className="font-mono tabular-nums">
                {stats?.staleCount ? (
                  <Link
                    to="/returns"
                    search={{ status: "all", type: "all", q: "", from: "", to: "", page: 1, stale: true }}
                    className="text-red-500 hover:underline"
                  >
                    {stats.staleCount}
                  </Link>
                ) : (
                  0
                )}
              </dd>
            </div>
            <div className="flex items-baseline justify-between gap-3">
              <dt className="text-muted-foreground">Prosječno rješavanje</dt>
              <dd className="font-mono tabular-nums">
                {stats?.avgResolutionDays === null || stats?.avgResolutionDays === undefined
                  ? "—"
                  : formatDaysAgo(stats.avgResolutionDays)}
              </dd>
            </div>
          </dl>
        </div>

        <div className="p-4 flex flex-col gap-3 min-w-0">
          <div className="flex items-baseline justify-between gap-3">
            <Eyebrow>Po danu</Eyebrow>
            <span className="text-[11px] text-muted-foreground">
              najviše u jednom danu: <span className="font-mono tabular-nums">{peak}</span>
            </span>
          </div>
          {isLoading ? (
            <Skeleton className="h-[180px] w-full" />
          ) : (
            <div className="h-[180px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trend} margin={{ top: 4, right: 8, bottom: 0, left: -16 }}>
                  <defs>
                    <linearGradient id="returnsTrend" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--foreground)" stopOpacity={0.22} />
                      <stop offset="100%" stopColor="var(--foreground)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="var(--foreground)" strokeOpacity={0.08} vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                    tickLine={false}
                    axisLine={false}
                    minTickGap={24}
                  />
                  <YAxis
                    allowDecimals={false}
                    width={44}
                    tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip content={<TrendTooltip />} cursor={{ stroke: "var(--foreground)", strokeOpacity: 0.2 }} />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke="var(--foreground)"
                    strokeWidth={1.5}
                    fill="url(#returnsTrend)"
                    isAnimationActive={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel
          title="Zašto se vraća"
          note={
            reasonTotal > 0 && sizeReasonCount > 0
              ? `veličina: ${share(sizeReasonCount, reasonTotal)} navedenih razloga`
              : undefined
          }
        >
          <RankList rows={reasonRows} total={reasonTotal} isLoading={isLoading} />
        </Panel>

        <div className="flex flex-col gap-4 min-w-0">
          <Panel title="Status zahtjeva">
            <SplitBar
              isLoading={isLoading}
              rows={(stats?.byStatus ?? []).map((r) => ({
                key: r.status,
                label: STATUS_LABEL[r.status] || r.status,
                count: r.count,
                color: STATUS_BAR[r.status] || "bg-foreground/40",
              }))}
            />
          </Panel>
          <Panel title="Tip zahtjeva">
            <SplitBar
              isLoading={isLoading}
              rows={(stats?.byType ?? []).map((r, i) => ({
                key: r.requestType,
                label: REQUEST_TYPE_LABEL[r.requestType] || r.requestType,
                count: r.count,
                color: ["bg-foreground/70", "bg-foreground/45", "bg-foreground/25"][i] || "bg-foreground/15",
              }))}
            />
          </Panel>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel title="Najčešće vraćeni proizvodi">
          {!isLoading && linked.length === 0 && unlinked.length > 0 ? (
            <Empty>Nijedna stavka još nije povezana s proizvodom iz kataloga</Empty>
          ) : (
            <RankList
              rows={linked.map((p, i) => ({
                // Isti productId može doći iz dvije trgovine kad nema aktivne trgovine.
                key: `${p.productId}-${i}`,
                label: p.name,
                count: p.count,
              }))}
              total={productTotal}
              isLoading={isLoading}
            />
          )}
          {!isLoading && unlinked.length > 0 && (
            <div className="flex flex-col gap-3 pt-4 border-t border-foreground/10">
              <Eyebrow>Nepovezano s katalogom</Eyebrow>
              <RankList
                rows={unlinked.map((p) => ({ key: p.model, label: p.model, count: p.count }))}
                total={productTotal}
                isLoading={false}
              />
            </div>
          )}
        </Panel>

        <Panel title="Najčešće vraćene varijacije">
          <RankList
            rows={variations.map((v, i) => ({
              key: `${v.productId}-${v.variationId}-${i}`,
              label: v.name,
              count: v.count,
            }))}
            total={variationTotal}
            isLoading={isLoading}
          />
        </Panel>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel title="Smjer zamjene" note={flow.length > 0 ? "vraćeno → traženo" : undefined}>
          {isLoading ? (
            <RowsSkeleton rows={4} />
          ) : flow.length === 0 ? (
            <Empty>Nema povezanih zamjena u ovom periodu</Empty>
          ) : (
            <ul className="flex flex-col">
              {flow.map((f, i) => {
                const swap = variationSwap(f.from, f.to);
                return (
                  <li
                    key={`${f.from}-${f.to}-${i}`}
                    className="flex items-center gap-3 py-3 border-b border-foreground/10 last:border-b-0 min-w-0"
                  >
                    <div className="flex flex-col gap-1 min-w-0 flex-1">
                      {swap && (
                        <span className="text-[11px] text-muted-foreground truncate" title={swap.product}>
                          {swap.product}
                        </span>
                      )}
                      <div className="flex items-center gap-2.5 text-[13px] min-w-0">
                        <span className="text-muted-foreground truncate" title={swap ? swap.from : f.from}>
                          {swap ? swap.from : f.from}
                        </span>
                        <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        <span className="truncate" title={swap ? swap.to : f.to}>
                          {swap ? swap.to : f.to}
                        </span>
                      </div>
                    </div>
                    <span className="font-mono text-[13px] tabular-nums text-muted-foreground shrink-0">
                      ×{f.count}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </Panel>

        <Panel title="Stopa povrata" note={rateData?.items?.length ? "vraćeno / prodano" : undefined}>
          <RateTable rows={rateData?.items ?? []} isLoading={rateLoading} error={rateError} hasStore={!!storeId} />
        </Panel>
      </div>
    </div>
  );
}

/** Udio povrata u prodaji po proizvodu. Traži webhook trgovinu — bez nje endpoint javlja `not_webhook`. */
function RateTable({
  rows,
  isLoading,
  error,
  hasStore,
}: {
  rows: ReturnRateRow[];
  isLoading: boolean;
  error: any;
  hasStore: boolean;
}) {
  if (!hasStore) return <Empty>Odaberi trgovinu da vidiš stopu povrata</Empty>;
  if (error) return <Empty>{error.message || "Podaci nisu dostupni"}</Empty>;
  if (isLoading) return <RowsSkeleton rows={4} />;
  if (rows.length === 0) return <Empty />;
  return (
    <ul className="flex flex-col">
      {rows.map((r) => (
        <li
          key={r.productId}
          className="flex items-center gap-3 py-2.5 border-b border-foreground/10 last:border-b-0 min-w-0"
        >
          <div className="flex flex-col gap-0.5 min-w-0 flex-1">
            <span className="text-[13px] truncate" title={r.name}>
              {r.name}
            </span>
            <span className="font-mono text-[11px] tabular-nums text-muted-foreground">
              {r.returned} / {r.sold} prodano
            </span>
          </div>
          <span className="font-mono text-[15px] tabular-nums shrink-0">
            {r.sold > 0 ? `${Math.round(r.rate * 1000) / 10}%` : "—"}
          </span>
        </li>
      ))}
    </ul>
  );
}
